// ─── MFML SPECIES TRAITS & LORE ─────────────────────────────────────────────
// This file defines MFML-universe-specific species rules that override
// popular media assumptions. The AI MUST follow these rules, not Hollywood/TV rules.
//
// Also includes cross-species interaction rules (how species relate to each other,
// political dynamics, biological interactions like landbonds, mate bonds, etc.)

// ─── CONDENSED SPECIES RULES ────────────────────────────────────────────────
// Only the rules that DIFFER from popular media. Character profiles handle
// personality/culture. This keeps token count low while preventing the AI
// from applying Hollywood tropes that contradict MFML canon.

export const SPECIES_TRAITS = {

  vampire: {
    name: 'Vampire',
    matchSpecies: ['vampire', 'vampire elder', 'hybrid vampire', 'vampire/siren hybrid', 'vampire/werecheetah hybrid', 'half-vampire'],
    traits: [
      'CAN sleep, CAN eat food, CAN go in sunlight — NOT Hollywood rules.',
      'Fangs extend when emotional. Enhanced senses, speed, strength. Can hear heartbeats.',
      'Vampire hierarchy: Elder Council > Kings/Queens > Noble Houses > Turned. Elders are 1000+ years old.',
      'CAN have children. Progeny retain traits of original species. Blood drinking can be intimate.',
    ],
  },

  siren: {
    name: 'Siren',
    matchSpecies: ['siren', 'siren queen', 'atlantic royalty', 'landbond'],
    traits: [
      'Human form + siren form (tail, scales). Can breathe on land AND underwater.',
      'Siren song influences/calms/attacks. Royal sirens have the strongest song.',
      'LANDBOND: Sacred cosmic bond tying a siren to a land-dweller across timelines. Deeper than marriage.',
      'Atlantic Siren Kingdom is a full underwater civilization with royal family and politics.',
    ],
  },

  werewolf: {
    name: 'Werewolf',
    matchSpecies: ['werewolf', 'alpha', 'beta', 'pack', 'wolf'],
    traits: [
      'MATE BOND: Fated mates recognized by instinct (scent, pull). Sacred, nearly unbreakable.',
      'CAN shift at will — full moons enhance power but do NOT force transformation.',
      'Pack hierarchy: Alpha > Beta > ranked wolves > omega. Alpha voice compels obedience.',
      'Run HOT (elevated body temp). Mate-marking is a neck bite — intimate, permanent, public.',
    ],
  },

  fae: {
    name: 'Fae / Fairy',
    matchSpecies: ['fae', 'fairy', 'high fae', 'fairy prince', 'fairy princess', 'elf', 'celestial'],
    traits: [
      'High Fae are powerful immortals distinct from lesser fairies. Soulmate bonds span lifetimes.',
      'Cannot lie directly but CAN deceive through omission. Iron is harmful to lesser fae; High Fae are more resistant.',
      'Wings manifest with magic or strong emotion. Fae deals and debts are magically binding — breaking them has consequences.',
      'FAE REALM SOCIAL NORMS (alternate realm universe): In the true fae realm, bonding customs differ fundamentally from human morality. Royal sibling bonds can carry deep romantic and intimate dimensions — this is considered sacred, not taboo, in fae culture. The bond between royal siblings is often the most powerful magical connection possible, stronger than most soulmate bonds. Fae royals who share a realm-bond feel each other\'s emotions across distances.',
      'FAE REALM POWER STRUCTURE: Fae courts are hierarchical with absolute loyalty to the ruling bloodline. Palace politics involve strategic secrets, hidden identities, and information as currency. Characters placed in the human world are almost always in hiding for protection.',
      'FAE REALM AND HUMAN WORLD: When fae royals are hidden in the human world, they are given cover identities (cousin, friend, distant family). They do NOT know their true identity until it is revealed to them. The fae guardian/protector sent with them DOES know the truth.',
    ],
  },

  god: {
    name: 'God / Demigod / Deity',
    matchSpecies: ['god', 'demigod', 'deity', 'goddess', 'olymp', 'norse', 'cupid', 'ares', 'hades', 'apollo'],
    traits: [
      'Greek gods are REAL and active in MFML. Powers vary by domain (Ares=war, Apollo=prophecy, Cupid=desire).',
      'The Fates (Atropos, Lachesis, Clotho) control destiny — among the most powerful beings.',
      'Gods CAN be hurt but are extremely hard to kill. Can take mortal form or reincarnate.',
    ],
  },

  witch: {
    name: 'Witch / Wizard',
    matchSpecies: ['witch', 'wizard', 'half-wizard'],
    traits: [
      'Magic is learned and practiced. Potions, spells, enchantments, curses all functional.',
      'Witch/werewolf hybrids exist. Half-wizard progeny retain magic alongside vampire nature.',
    ],
  },

  demon: {
    name: 'Demon',
    matchSpecies: ['demon', 'sin of', 'demon prince'],
    traits: [
      'Own species — not inherently evil, morally complex. Some tied to specific sins.',
      'Ancient demons (1000+ yrs) are politically significant. Demon courts parallel other governments.',
    ],
  },

  human: {
    name: 'Human',
    matchSpecies: ['human', 'scholarship', 'assassin'],
    traits: [
      'Aware of (or discovering) the supernatural world. Can be turned/bonded/transformed.',
    ],
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// CROSS-SPECIES INTERACTION RULES
// ═══════════════════════════════════════════════════════════════════════════
// Condensed cross-species rules — only include when two different species are interacting
export const CROSS_SPECIES_RULES = [
  'Siren blood is uniquely appealing to vampires — creates attraction and danger. Vampire/Siren hybrids possible.',
  'Vampires and werewolves have an old blood feud. Cross-species alliances (like Bryce joining a pack) are extraordinary.',
  'Evelyn converting from vampire to werewolf for Cruz was unprecedented — caused massive political fallout.',
  'Gods consider themselves above supernatural politics but meddle constantly. Demigods draw divine attention.',
  'Fae bonds are cosmic-level and override most other magical connections.',
  'Cross-species relationships always carry social, political, and biological complications.',
];

// ═══════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

// The `species` column in character_overrides holds authored PROSE, not a
// short label — "Goddess of Fate (The Cutter) — one of the three Moirai…"
// runs on for a thousand characters. Plain `lower.includes(match)` over that
// gave 26 of 70 live characters the WRONG ruleset (measured 2026-08-10):
//
//   atropos, lachesis  "she introduces herself as…"  → "elf" → FAE
//   moros, lex, riku, libion, ramiel, lonnie, rika, levi, dike, lacie
//                      "himself" / "itself"          → "elf" → FAE
//   yuri_rekovich      "emphatically NOT a vampire"  → VAMPIRE
//   ben                "explicitly NOT a vampire"    → VAMPIRE
//   eva, hunter, maxen "…compared to vampires"       → VAMPIRE over Werewolf
//   ryoto (Dragon), york (forest spirit), jane (Witch), milo, renee
//                      another species named in passing deep in the prose
//
// Three things fix it, in order:
//   1. Match against the LABEL — the part before the first dash, period or
//      parenthesis — not the whole essay. A species mentioned in paragraph
//      four is not this character's species.
//   2. Match at a word START, not anywhere. "\belf" does not fire inside
//      "herself" but still fires on "Light Elf". A plain \b…\b word boundary
//      cannot be used: it would break "Goddess" → god, which is correct.
//   3. Honour negation. "explicitly NOT a vampire" is the character telling
//      us what they are not.

const NEGATION_WINDOW = 24; // chars before the needle to scan for a negation

// Everything before the first em-dash, en-dash, period or newline, capped so
// a run-on first clause cannot swallow the whole entry. A parenthetical is
// KEPT: it is part of the label and often carries the real answer — ezra's
// override reads "Hybrid (Half vampire, half wizard)", and cutting at the
// paren left the useless word "Hybrid".
export function speciesLabelOf(speciesString) {
  const raw = String(speciesString || '');
  const cut = raw.search(/[—–.\n]/);
  return (cut === -1 ? raw : raw.slice(0, cut)).trim().slice(0, 80);
}

const escapeRe = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Index of the first non-negated occurrence of the needle at a word start,
// or -1. Word START rather than a full \b…\b word boundary: "\belf" must not
// fire inside "herself", but "\bgod" must still fire on "Goddess".
//
// EXPORTED because species_lore matches species strings too, and had its own
// unguarded `text.includes()` — so the bug this function exists to prevent was
// live in the other matcher the whole time ("t-witch-es" → Witch canon,
// "explicitly NOT a vampire" → Vampire canon). Two matchers over the same
// field is the "contract in two places" pattern in ROOT-CAUSES; one shared
// assertion is how it stops being two.
export function assertionIndex(label, needle) {
  const re = new RegExp(`\\b${escapeRe(needle)}`, 'gi');
  let m;
  while ((m = re.exec(label)) !== null) {
    const before = label.slice(Math.max(0, m.index - NEGATION_WINDOW), m.index);
    if (!/\b(not|non|never|isn't|is not|aren't|rather than|except|unlike)\b[\s\w'’-]*$/i.test(before)) {
      return m.index;
    }
  }
  return -1;
}

// Declaration order in SPECIES_TRAITS decides a genuine hybrid, exactly as it
// did before. That ordering is arbitrary, but a character written as
// "Witch/Werewolf Hybrid" really is both and the function can only return one
// ruleset — so changing which one it picks is churn, not a fix. Keeping it
// means this change moves ONLY the characters that were actually wrong.
// Picking a real winner for hybrids means returning both, which is a
// different change with a different blast radius.
function firstAssertedIn(label) {
  for (const [, data] of Object.entries(SPECIES_TRAITS)) {
    if (data.matchSpecies.some(needle => assertionIndex(label, needle) !== -1)) return data;
  }
  return null;
}

/**
 * Find the matching species traits for a character based on their species string.
 *
 * "A → B" is a transformation the archive writes a lot ("Human → Siren Queen",
 * "Vampire → Werewolf"). What the character IS now is on the right of the last
 * arrow, so that side is tried first and the full label is the fallback.
 */
export function getSpeciesTraitsForCharacter(speciesString) {
  if (!speciesString) return null;
  const label = speciesLabelOf(speciesString);
  if (!label) return null;

  const arrow = label.lastIndexOf('→');
  if (arrow !== -1) {
    const became = label.slice(arrow + 1).trim();
    const after = became ? firstAssertedIn(became) : null;
    if (after) return after;
  }
  return firstAssertedIn(label);
}

/**
 * Get species traits for BOTH characters in a conversation and any relevant
 * cross-species interaction rules.
 */
export function getSpeciesContextForPair(charSpecies, targetSpecies) {
  const charTraits = getSpeciesTraitsForCharacter(charSpecies);
  const targetTraits = targetSpecies ? getSpeciesTraitsForCharacter(targetSpecies) : null;

  let section = '';

  if (charTraits) {
    section += `\nSPECIES RULES (${charTraits.name} — MFML canon, NOT Hollywood):\n`;
    section += charTraits.traits.map(t => `- ${t}`).join('\n');
  }

  if (targetTraits && targetTraits.name !== charTraits?.name) {
    section += `\n\n${targetTraits.name} (who you're talking to):\n`;
    section += targetTraits.traits.slice(0, 2).map(t => `- ${t}`).join('\n');
  }

  // Add relevant cross-species rules
  if (charTraits && targetTraits && charTraits.name !== targetTraits.name) {
    const relevantRules = CROSS_SPECIES_RULES.filter(rule => {
      const ruleLower = rule.toLowerCase();
      const charKey = charTraits.name.toLowerCase().split(' ')[0];
      const targetKey = targetTraits.name.toLowerCase().split(' ')[0];
      return ruleLower.includes(charKey) && (ruleLower.includes(targetKey) || ruleLower.includes('mortal') || ruleLower.includes('other'));
    });

    if (relevantRules.length > 0) {
      section += `\n\nCROSS-SPECIES DYNAMICS (${charTraits.name} ↔ ${targetTraits.name}):\n`;
      section += relevantRules.map(r => `- ${r}`).join('\n');
    }
  }

  return section;
}

/**
 * Get species traits for a single character (simpler version for RP scenes).
 */
export function getSpeciesPromptSection(speciesString) {
  const traits = getSpeciesTraitsForCharacter(speciesString);
  if (!traits) return '';

  let section = `\nSPECIES RULES (${traits.name} — MFML canon, NOT Hollywood):\n`;
  section += traits.traits.map(t => `- ${t}`).join('\n');
  return section;
}
